import React, { useState } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag, faTimes } from '@fortawesome/free-solid-svg-icons';
import { validatePromoCodeForDeposit, calculateOrderTotalsWithPromo } from '../../libs/promoCodeLib';
import { renderPrice } from '../../libs/cartLib';

export default function DepositPromoCodeInput({ 
  items, 
  currency = 'GTQ',
  show = null,
  onPromoCodeApplied, 
  onPromoCodeRemoved,
  onTotalsUpdated 
}) {
  const [promoCode, setPromoCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [appliedPromoCode, setAppliedPromoCode] = useState(null);
  const [orderTotals, setOrderTotals] = useState(null);

  // Check if a promo code is already applied
  const hasAppliedPromoCode = appliedPromoCode !== null;

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) return;

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate promo code
      const validatedPromoCode = await validatePromoCodeForDeposit(promoCode.trim(), items, show);
      
      // Calculate order totals with promo code
      const totals = calculateOrderTotalsWithPromo(items, validatedPromoCode);
      
      setAppliedPromoCode(validatedPromoCode);
      setOrderTotals(totals);
      setSuccess('¡Código promocional aplicado exitosamente!');
      setPromoCode('');

      // Notify parent components
      if (onPromoCodeApplied) {
        onPromoCodeApplied(validatedPromoCode);
      }
      if (onTotalsUpdated) {
        onTotalsUpdated(totals);
      }
    } catch (err) {
      console.error('Error applying promo code:', err);
      let errorMessage = 'Error al aplicar el código promocional';

      if (err.response && err.response.data) {
        if (err.response.data.error === 'PROMO_CODE_NOT_FOUND') {
          errorMessage = 'Código promocional no encontrado';
        } else if (err.response.data.error === 'PROMO_CODE_INACTIVE') {
          errorMessage = 'Código promocional inactivo';
        } else if (err.response.data.error === 'PROMO_CODE_USAGE_LIMIT_EXCEEDED') {
          errorMessage = 'Límite de uso del código promocional excedido';
        } else if (err.response.data.error === 'PROMO_CODE_EXPIRED') {
          errorMessage = 'Código promocional expirado';
        } else if (err.response.data.error === 'PROMO_CODE_NOT_APPLICABLE') {
          errorMessage = 'Código promocional no aplicable a este pedido';
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemovePromoCode = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Calculate order totals without promo code
      const totals = calculateOrderTotalsWithPromo(items, null);
      
      setAppliedPromoCode(null);
      setOrderTotals(totals);

      // Notify parent components
      if (onPromoCodeRemoved) {
        onPromoCodeRemoved();
      }
      if (onTotalsUpdated) {
        onTotalsUpdated(totals);
      }
    } catch (err) {
      console.error('Error removing promo code:', err);
      setError('Error al remover el código promocional');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApplyPromoCode();
    }
  };

  return (
    <div className="promo-code-section">
      {!hasAppliedPromoCode && (
        <div className="promo-code-input-group">
          <Form.Control
            type="text"
            placeholder="Ingresa tu código promocional"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            onKeyPress={handleKeyPress}
            className="promo-code-input"
            disabled={isLoading}
          />
          <Button
            variant="outline-primary"
            onClick={handleApplyPromoCode}
            disabled={!promoCode.trim() || isLoading}
            className="promo-code-apply-btn"
          >
            <FontAwesomeIcon icon={faTag} /> Aplicar
          </Button>
        </div>
      )}

      {error && <Alert variant="danger" className="mt-2">{error}</Alert>}
      {success && <Alert variant="success" className="mt-2">{success}</Alert>}

      {appliedPromoCode && (
        <div className="applied-promo-code">
          <span className="promo-code-text">
            <FontAwesomeIcon icon={faTag} /> {appliedPromoCode.code}
          </span>
          <span className="promo-code-discount">
            {appliedPromoCode.discount_type === 'PERCENT'
              ? `${appliedPromoCode.discount_value}%`
              : `-${renderPrice({ currency }, appliedPromoCode.discount_value)}`
            }
          </span>
          <Button
            variant="link"
            size="sm"
            onClick={handleRemovePromoCode}
            className="promo-code-remove-btn"
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      )}

      {orderTotals && (
        <div className="order-totals-preview">
          <div className="total-row">
            <span>Subtotal:</span>
            <span>{renderPrice({ currency }, orderTotals.subtotal)}</span>
          </div>
          {orderTotals.total_discount > 0 && (
            <div className="total-row discount">
              <span>Descuento:</span>
              <span>-{renderPrice({ currency }, orderTotals.total_discount)}</span>
            </div>
          )}
          <div className="total-row final">
            <span>Total:</span>
            <span>{renderPrice({ currency }, orderTotals.total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
